'use strict'

const IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/
const REVERSE_TARGET_PREFIX = 'user:'

function loginSpreadSheetIn (components) {

  for (const comp of components || []) {

    if (!comp || typeof comp !== 'object') continue

    if ((comp.type === 'login' || comp.type === 'registration') && comp.spreadSheet) return comp.spreadSheet

    const nested = loginSpreadSheetIn(comp.components) || loginSpreadSheetIn(comp.children)

    if (nested) return nested
  }

  return null
}

function pageLabel (page) {

  if (!page || typeof page !== 'object') return 'untitled'

  const title = typeof page.title === 'string' ? page.title.trim() : ''

  if (title) return title

  const uniqueTitle = typeof page.uniqueTitle === 'string' ? page.uniqueTitle.trim() : ''

  return uniqueTitle || 'untitled'
}

function addLoginSpreadSheetUse (index, spreadSheet, page) {

  if (typeof spreadSheet !== 'string' || !spreadSheet) return

  if (!index[spreadSheet]) index[spreadSheet] = []

  const uniqueTitle = page && typeof page.uniqueTitle === 'string' ? page.uniqueTitle : ''
  const title = pageLabel(page)
  const already = index[spreadSheet].some(item => item.uniqueTitle === uniqueTitle && item.title === title)

  if (!already) index[spreadSheet].push({ uniqueTitle, title })
}

function walkComponentsForLoginSheets (components, index, page) {

  for (const comp of components || []) {

    if (!comp || typeof comp !== 'object') continue

    if ((comp.type === 'login' || comp.type === 'registration') && comp.spreadSheet) addLoginSpreadSheetUse(index, comp.spreadSheet, page)

    walkComponentsForLoginSheets(comp.components, index, page)
    walkComponentsForLoginSheets(comp.children, index, page)
  }
}

function walkPagesForLoginSheets (pages, index, seenPages) {

  for (const page of pages || []) {

    if (!page || typeof page !== 'object' || seenPages.has(page)) continue

    seenPages.add(page)
    walkComponentsForLoginSheets(page.components, index, page)

    if (Array.isArray(page.children)) walkPagesForLoginSheets(page.children, index, seenPages)
  }
}

function loginSpreadSheetIndex (pages) {

  const index = {}

  walkPagesForLoginSheets(Array.isArray(pages) ? pages : [], index, new Set())

  return index
}

function loginSpreadSheetNote (pagesForSheet) {

  const titles = []
  const seen = new Set()

  for (const page of pagesForSheet || []) {

    const title = page && (page.title || page.uniqueTitle)

    if (!title || seen.has(title)) continue

    seen.add(title)
    titles.push(title)
  }

  if (!titles.length) return 'Used for login'

  if (titles.length === 1) return `Used for login on ${titles[0]}`

  if (titles.length === 2) return `Used for login on ${titles[0]} and ${titles[1]}`

  return `Used for login on ${titles.slice(0, -1).join(', ')}, and ${titles[titles.length - 1]}`
}

function findTopLevelLoginSpreadSheet (pages) {

  const list = (Array.isArray(pages) ? pages : []).filter(page => page && typeof page === 'object')
  const roots = list.filter(page => !page.parentUniqueTitle)
  const nested = list.filter(page => page.parentUniqueTitle)

  for (const page of [...roots, ...nested]) {

    const spreadSheet = loginSpreadSheetIn(page.components)

    if (spreadSheet) return spreadSheet
  }

  return null
}

function relationshipColumnsTo (spreadsheet, relatedSpreadSheet) {

  if (!spreadsheet || !relatedSpreadSheet) return []

  return (spreadsheet.columns || []).filter(column => {

    return column && column.type === 'relationship' && column.relatedSpreadSheet === relatedSpreadSheet && column.uniqueTitle
  })
}

function loginProtectedUserLinks ({ pages, spreadsheet, spreadsheets }) {

  const userSpreadSheet = findTopLevelLoginSpreadSheet(pages)
  const enabled = !!userSpreadSheet && !!spreadsheet
  const userSheet = enabled
    ? (spreadsheets || []).find(item => item && item.uniqueTitle === userSpreadSheet)
    : null

  return {
    enabled,
    userSpreadSheet: enabled ? userSpreadSheet : null,
    forward: enabled ? relationshipColumnsTo(spreadsheet, userSpreadSheet) : [],
    reverse: enabled ? relationshipColumnsTo(userSheet, spreadsheet.uniqueTitle) : []
  }
}

function isReverseTargetField (field) {

  return typeof field === 'string' && field.startsWith(REVERSE_TARGET_PREFIX)
}

function reverseTargetField (uniqueTitle) {

  return `${REVERSE_TARGET_PREFIX}${uniqueTitle}`
}

function targetFieldName (field) {

  if (isReverseTargetField(field)) return field.slice(REVERSE_TARGET_PREFIX.length)

  return field || ''
}

function editorFieldForAction (action) {

  if (!action) return ''

  if (action.linkFrom === 'loggedInUser' && action.field && !isReverseTargetField(action.field)) {

    return reverseTargetField(action.field)
  }

  return action.field || ''
}

function normalizeLoggedInUserAction (action, userSpreadSheet) {

  if (!action) return action

  const reverse = isReverseTargetField(action.field)
  const next = { ...action, field: targetFieldName(action.field) }

  if (reverse || next.valueSource === 'loggedInUser') {

    next.valueSource = 'loggedInUser'
    next.linkFrom = reverse ? 'loggedInUser' : 'record'
    next.relSelectedRecords = []
    next.userSpreadSheet = userSpreadSheet || next.userSpreadSheet || null

    return next
  }

  if (next.linkFrom === 'loggedInUser') next.linkFrom = 'record'

  return next
}

function loggedInUserRelationshipDefault (action) {

  if (!action || !action.setValue || action.valueSource !== 'loggedInUser') return null
  if (!IDENTIFIER.test(action.field || '')) return null

  return {
    field: action.field,
    loggedInUser: true,
    linkFrom: action.linkFrom === 'loggedInUser' ? 'loggedInUser' : 'record',
    userSpreadSheet: action.userSpreadSheet || null
  }
}

function loggedInUserFieldLists (relationshipDefaults) {

  const forward = []
  const reverse = []

  for (const item of relationshipDefaults || []) {

    if (!item || !item.loggedInUser || !IDENTIFIER.test(item.field || '')) continue

    if (item.linkFrom === 'loggedInUser') reverse.push(item.field)
    else forward.push(item.field)
  }

  return { forward, reverse }
}

function appendLoggedInUserQuery (url, lists) {

  const params = []
  const forward = lists && lists.forward || []
  const reverse = lists && lists.reverse || []

  if (forward.length) params.push(`loggedInUserFields=${encodeURIComponent(forward.join(','))}`)
  if (reverse.length) params.push(`loggedInUserReverseFields=${encodeURIComponent(reverse.join(','))}`)
  if (!params.length) return url

  return `${url}${url.includes('?') ? '&' : '?'}${params.join('&')}`
}

function fieldListFromQuery (value) {

  if (!value || typeof value !== 'string') return []

  return value.split(',').map(part => part.trim()).filter(part => IDENTIFIER.test(part))
}

module.exports = {
  loginSpreadSheetIndex,
  loginSpreadSheetNote,
  findTopLevelLoginSpreadSheet,
  loginProtectedUserLinks,
  isReverseTargetField,
  reverseTargetField,
  editorFieldForAction,
  normalizeLoggedInUserAction,
  loggedInUserRelationshipDefault,
  loggedInUserFieldLists,
  appendLoggedInUserQuery,
  fieldListFromQuery
}
